import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { StatusDot, type StatusTone } from "../../atoms/StatusDot/index.js";

/** `vertical` (default) stacks the entries down a spine; `horizontal` puts
 *  the markers on one spine across the row with each entry's text under its
 *  marker. The same word as Stepper and Tabs. */
export type TimelineOrientation = "vertical" | "horizontal";
/** `comfortable` (default) is today's look; `compact` is the dense activity
 *  list: 12px markers, `text-dense-1`, 4px rows and an unbroken spine. The
 *  same word as DataTable and List. */
export type TimelineDensity = "comfortable" | "compact";

export interface TimelineItemDef {
  id: string;
  /** Time/date label shown above the entry body. */
  time?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  tone?: StatusTone;
  /** Replaces this item's StatusDot inside the aria-hidden marker column: an
   *  icon, `<Spinner aria-hidden />` or an Avatar. It is decoration only, so
   *  the title or description must still say the item's state. Keep every
   *  marker in one list the same size so the spine stays straight; `compact`
   *  centres it in a 12px box. */
  markerSlot?: React.ReactNode;
}

export interface TimelineProps extends React.OlHTMLAttributes<HTMLOListElement> {
  items: TimelineItemDef[];
  orientation?: TimelineOrientation;
  density?: TimelineDensity;
}

const TEXT: Record<
  TimelineDensity,
  { time: string; title: string; description: string }
> = {
  comfortable: {
    time: "text-dense-1 uppercase tracking-wider text-pb-text-muted pb-tabular",
    title: "text-sm font-medium text-pb-text",
    description: "mt-0.5 text-xs leading-relaxed text-pb-text-secondary",
  },
  compact: {
    time: "text-dense-1 uppercase tracking-wider text-pb-text-muted pb-tabular",
    title: "text-dense-1 text-pb-text",
    description: "text-dense-1 text-pb-text-muted",
  },
};

/* The item's marker. The comfortable dot keeps today's exact element; a slot
   takes the dot's place and offset. Compact centres either in a 12px box. */
function marker(
  item: TimelineItemDef,
  density: TimelineDensity,
  offset: string,
): React.ReactNode {
  const tone = item.tone ?? "neutral";
  if (density === "compact") {
    return (
      <span
        className={cn(
          "flex h-3 w-3 shrink-0 items-center justify-center",
          offset,
        )}
      >
        {item.markerSlot ?? <StatusDot tone={tone} size="sm" />}
      </span>
    );
  }
  if (item.markerSlot == null) {
    return <StatusDot tone={tone} size="md" className={offset} />;
  }
  return <span className={cn("flex shrink-0", offset)}>{item.markerSlot}</span>;
}

/* Vertical event timeline (guide §10 "Timeline", register #12). Pure data
   display: ordered list semantics, StatusDot markers on a hairline spine.
   `orientation="horizontal"` lays the same list across one spine with logical
   insets, so it mirrors under RTL. */
export const Timeline = forwardRef<HTMLOListElement, TimelineProps>(
  function Timeline(
    {
      items,
      orientation = "vertical",
      density = "comfortable",
      className,
      ...rest
    },
    ref,
  ) {
    const horizontal = orientation === "horizontal";
    const compact = density === "compact";
    const text = TEXT[density];
    return (
      <ol
        ref={ref}
        className={cn(
          "relative m-0 list-none p-0",
          horizontal && "flex items-start",
          className,
        )}
        {...rest}
      >
        {items.map((item, i) => {
          const last = i === items.length - 1;
          const body = (
            <>
              {item.time && <div className={text.time}>{item.time}</div>}
              <div className={text.title}>{item.title}</div>
              {item.description && (
                <div className={text.description}>{item.description}</div>
              )}
            </>
          );
          if (horizontal) {
            return (
              <li
                key={item.id}
                className="relative flex min-w-0 flex-1 flex-col items-center text-center"
              >
                {/* The spine runs from the previous marker's centre to this
                    one's; end-1/2 is logical, so RTL mirrors it. */}
                <span
                  className="relative flex w-full justify-center"
                  aria-hidden="true"
                >
                  {i > 0 && (
                    <span className="absolute end-1/2 top-1/2 h-px w-full bg-pb-border" />
                  )}
                  {marker(item, density, "relative")}
                </span>
                <div className={cn("max-w-full", compact ? "mt-1" : "mt-2")}>
                  {body}
                </div>
              </li>
            );
          }
          if (compact) {
            /* The row gap sits inside the text column, so the stretched
               marker column spans it and the spine never breaks: a 2px stub
               above the marker (it centres the 12px marker on the 16px line)
               meets the previous row's spine. */
            return (
              <li key={item.id} className="relative flex gap-1.5">
                <span className="flex flex-col items-center" aria-hidden="true">
                  <span className={cn("h-0.5 w-px", i > 0 && "bg-pb-border")} />
                  {marker(item, density, "")}
                  {!last && <span className="w-px flex-1 bg-pb-border" />}
                </span>
                <div className={cn("min-w-0 flex-1", !last && "pb-1")}>
                  {body}
                </div>
              </li>
            );
          }
          return (
            <li key={item.id} className="relative flex gap-3 pb-5 last:pb-0">
              <span className="flex flex-col items-center" aria-hidden="true">
                {marker(item, density, "mt-1")}
                {!last && <span className="mt-1 w-px flex-1 bg-pb-border" />}
              </span>
              <div className="min-w-0 flex-1">{body}</div>
            </li>
          );
        })}
      </ol>
    );
  },
);
